import { t, type MessageKey } from '../i18n/index.ts';
import type { IconName } from './Icon.tsx';

/**
 * What the search across the business finds (the server's `search.find`), and where each kind
 * of record opens. A record whose screen the person cannot open is left out, so a result always
 * leads somewhere.
 */

export type SearchKind = 'customer' | 'supplier' | 'product' | 'employee' | 'invoice' | 'payment' | 'order' | 'purchase_order' | 'project' | 'document';

export interface SearchHit {
  kind: SearchKind;
  id: string;
  title: string;
  subtitle: string | null;
  ref: string | null;
  state: string | null;
  parent: string | null;
}

type Can = (permission: string) => boolean;
const q = encodeURIComponent;

export const RECORD_KINDS: Record<SearchKind, { icon: IconName; label: MessageKey; link: (h: SearchHit, can: Can) => string | null }> = {
  customer: { icon: 'users', label: 'srch.k_customer', link: (h) => `/guests?tab=customers&open=${h.id}` },
  supplier: { icon: 'truck', label: 'srch.k_supplier', link: (h) => `/manage/purchasing?tab=suppliers&q=${q(h.title)}` },
  product: { icon: 'box', label: 'srch.k_product', link: (h, can) => (can('products.manage') ? `/manage/menu?q=${q(h.title)}` : null) },
  employee: { icon: 'contact', label: 'srch.k_employee', link: (h) => `/people?tab=team&open=${h.id}` },
  invoice: { icon: 'invoice', label: 'srch.k_invoice', link: (h) => `/invoices?open=${h.id}` },
  payment: { icon: 'card', label: 'srch.k_payment', link: (h) => `/payments/${h.id}` },
  order: { icon: 'receipt', label: 'srch.k_order', link: (h) => `/receipts?open=${h.id}` },
  purchase_order: { icon: 'cart', label: 'srch.k_purchase_order', link: (h) => `/manage/purchasing?open=${h.id}` },
  project: { icon: 'layers', label: 'srch.k_project', link: (h) => `/tasks/p/${h.id}` },
  document: { icon: 'file', label: 'srch.k_document', link: (h) => `/documents?${h.parent ? `folder=${h.parent}&` : ''}file=${h.id}` },
};

/** The second line of a found record: its number, then what it belongs to. */
export function hitHint(h: SearchHit): string {
  const parts = [h.ref, h.subtitle];
  if (h.state === 'archived') parts.push(t('srch.archived'));
  if (h.kind === 'invoice' && h.state?.startsWith('credit_')) parts.unshift(t('srch.credit_note'));
  if (h.kind === 'invoice' && h.state === 'draft') parts.push(t('srch.draft'));
  if (h.kind === 'order' && h.state === 'refund') parts.unshift(t('srch.refund'));
  return parts.filter(Boolean).join(' · ');
}

/** Starting something new from anywhere: each opens the right screen with its form ready. */
export interface QuickCreate {
  key: string;
  icon: IconName;
  label: string;
  to: string;
}
export function quickCreates(can: Can): QuickCreate[] {
  const all: (QuickCreate & { show: boolean })[] = [
    { key: 'invoice', icon: 'invoice', label: t('qc.invoice'), to: '/invoices?new=invoice', show: can('invoicing.manage') },
    { key: 'quote', icon: 'quote', label: t('qc.quote'), to: '/invoices?tab=quotes&new=quote', show: can('invoicing.manage') },
    { key: 'customer', icon: 'users', label: t('qc.customer'), to: '/guests?tab=customers&new=customer', show: can('customers.edit') },
    { key: 'lead', icon: 'trendUp', label: t('qc.lead'), to: '/crm?lead=new', show: can('crm.use') },
    { key: 'booking', icon: 'calendar', label: t('qc.booking'), to: '/guests?new=booking', show: can('reservations.manage') },
    { key: 'purchase_order', icon: 'cart', label: t('qc.purchase_order'), to: '/manage/purchasing?new=order', show: can('purchase.manage') },
    { key: 'bill', icon: 'receipt', label: t('qc.bill'), to: '/manage/purchasing?tab=bills&new=bill', show: can('purchase.manage') },
    { key: 'supplier', icon: 'truck', label: t('qc.supplier'), to: '/manage/purchasing?tab=suppliers&new=supplier', show: can('purchase.manage') },
    { key: 'product', icon: 'box', label: t('qc.product'), to: '/manage/menu?new=product', show: can('products.manage') },
  ];
  return all.filter((x) => x.show).map(({ show: _show, ...x }) => x);
}
